import { supabase } from '../../client';
import { logError } from '@/lib/sentry';
import type { DeliveryTemplate, ColumnMapping } from '@/utils/templateParser';

const TABLE_NAME = 'delivery_template';

/** DB row → DeliveryTemplate 변환 */
function toDeliveryTemplate(row: {
  id: number;
  name: string;
  column_mappings: ColumnMapping[];
  header_row_index: number;
  created_at: string;
  modified_at: string;
}): DeliveryTemplate {
  return {
    id: row.id,
    name: row.name,
    columnMappings: row.column_mappings,
    headerRowIndex: row.header_row_index,
    createdAt: row.created_at,
    modifiedAt: row.modified_at,
  };
}

/**
 * 사용자의 배송 템플릿 목록을 조회합니다.
 * 최신 수정순으로 정렬합니다.
 */
export async function getDeliveryTemplates(userId: string): Promise<DeliveryTemplate[]> {
  if (!userId) return [];

  const { data, error } = await supabase
    .from(TABLE_NAME)
    .select('id, name, column_mappings, header_row_index, created_at, modified_at')
    .eq('user_id', userId)
    .order('modified_at', { ascending: false });

  if (error) {
    logError('Failed to fetch delivery templates', error);
    throw error;
  }

  return (data ?? []).map(toDeliveryTemplate);
}

/**
 * 특정 배송 템플릿을 ID로 조회합니다.
 */
export async function getDeliveryTemplateById(userId: string, templateId: number): Promise<DeliveryTemplate | null> {
  if (!userId) return null;

  const { data, error } = await supabase
    .from(TABLE_NAME)
    .select('id, name, column_mappings, header_row_index, created_at, modified_at')
    .eq('user_id', userId)
    .eq('id', templateId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // not found
    logError('Failed to fetch delivery template by ID', error);
    throw error;
  }

  return toDeliveryTemplate(data);
}

/**
 * 배송 템플릿을 생성합니다.
 */
export async function createDeliveryTemplate(
  userId: string,
  template: Pick<DeliveryTemplate, 'name' | 'columnMappings' | 'headerRowIndex'>,
): Promise<DeliveryTemplate> {
  const { data, error } = await supabase
    .from(TABLE_NAME)
    .insert({
      user_id: userId,
      name: template.name,
      column_mappings: template.columnMappings as unknown as Record<string, unknown>[],
      header_row_index: template.headerRowIndex,
    })
    .select('id, name, column_mappings, header_row_index, created_at, modified_at')
    .single();

  if (error) {
    logError('Failed to create delivery template', error);
    throw error;
  }

  return toDeliveryTemplate(data);
}

/**
 * 배송 템플릿을 수정합니다.
 */
export async function updateDeliveryTemplate(
  userId: string,
  templateId: number,
  updates: Partial<Pick<DeliveryTemplate, 'name' | 'columnMappings' | 'headerRowIndex'>>,
): Promise<DeliveryTemplate> {
  const payload: Record<string, unknown> = {};
  if (updates.name !== undefined) payload.name = updates.name;
  if (updates.columnMappings !== undefined) payload.column_mappings = updates.columnMappings;
  if (updates.headerRowIndex !== undefined) payload.header_row_index = updates.headerRowIndex;

  const { data, error } = await supabase
    .from(TABLE_NAME)
    .update(payload)
    .eq('user_id', userId)
    .eq('id', templateId)
    .select('id, name, column_mappings, header_row_index, created_at, modified_at')
    .single();

  if (error) {
    logError('Failed to update delivery template', error);
    throw error;
  }

  return toDeliveryTemplate(data);
}

/**
 * 배송 템플릿을 삭제합니다.
 */
export async function deleteDeliveryTemplate(userId: string, templateId: number): Promise<void> {
  if (!userId) return;

  const { error } = await supabase
    .from(TABLE_NAME)
    .delete()
    .eq('user_id', userId)
    .eq('id', templateId);

  if (error) {
    logError('Failed to delete delivery template', error);
    throw error;
  }
}
